import i18n, { type InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';
import enShared from './locales/en/shared.json';
import viCommon from './locales/vi/common.json';
import viShared from './locales/vi/shared.json';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
  isSupportedLocale,
} from './types';

export const LOCALE_STORAGE_KEY = 'aaas-locale';

const initOptions: InitOptions = {
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...SUPPORTED_LOCALES],
  defaultNS: 'common',
  ns: ['common', 'shared'],
  interpolation: {
    escapeValue: false,
  },
  detection: {
    // Priority: URL `?lang=` → localStorage → browser → fallback.
    order: ['querystring', 'localStorage', 'navigator'],
    lookupQuerystring: 'lang',
    lookupLocalStorage: LOCALE_STORAGE_KEY,
    caches: ['localStorage'],
  },
  resources: {
    en: { common: enCommon, shared: enShared },
    vi: { common: viCommon, shared: viShared },
  },
  returnNull: false,
};

let initialized = false;

export function initI18n(): typeof i18n {
  if (initialized) return i18n;
  initialized = true;
  // Ensure the chain includes our detector + React bindings exactly once.
  void i18n.use(LanguageDetector).use(initReactI18next).init(initOptions);
  return i18n;
}

/**
 * Register an app-specific namespace bundle. Call from an app's bootstrap
 * before the first render that needs the strings (e.g. inside main.tsx right
 * after initI18n()). Safe to call multiple times.
 */
export function registerAppNamespace(
  namespace: string,
  resources: Record<SupportedLocale, Record<string, unknown>>,
): void {
  initI18n();
  for (const locale of SUPPORTED_LOCALES) {
    const bundle = resources[locale];
    if (bundle) {
      i18n.addResourceBundle(locale, namespace, bundle, true, true);
    }
  }
}

/**
 * Current UI locale, narrowed to a SupportedLocale. Safe to call before
 * initI18n() — returns DEFAULT_LOCALE in that case.
 */
export function getCurrentLocale(): SupportedLocale {
  const raw = i18n.language ?? DEFAULT_LOCALE;
  return isSupportedLocale(raw) ? raw : DEFAULT_LOCALE;
}

export { i18n };
